import { RxState } from '@rx-angular/state';
import { TranslateModule } from '@ngx-translate/core';
import { AVATAR_WIDTH } from '../../../constants';
import { TranslationModule } from '../../../i18n/translation-module';
import { CollapsibleSectionComponent } from '../../collapsible-section/collapsible-section.component';
import { ActivityInterface } from '@vendasta/crm';
import { CommonModule } from '@angular/common';
import { Component, computed, Inject, Input, Signal, signal, WritableSignal } from '@angular/core';
import { CrmFieldService, StandardIds, SystemFieldIds } from '../../../shared-services/crm-services/field.service';
import { MatCardModule } from '@angular/material/card';
import { DateFormat } from '@vendasta/galaxy/utility/date-utils';
import { GalaxyAvatarModule } from '@vendasta/galaxy/avatar';
import { SanitizeHtmlPipe } from './sanitize-html.pipe';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';
import { CrmDependencies, CrmInjectionToken } from '../../../tokens-and-interfaces';

@Component({
  selector: 'crm-activity-note',
  templateUrl: './note.component.html',
  styleUrls: ['./note.component.scss'],
  providers: [RxState],
  imports: [
    CommonModule,
    TranslateModule,
    TranslationModule,
    CollapsibleSectionComponent,
    MatCardModule,
    GalaxyAvatarModule,
    SanitizeHtmlPipe,
  ],
})
export class CrmActivityNoteComponent {
  private _activity: WritableSignal<ActivityInterface> = signal({});
  @Input({ required: true }) set activity(activity: ActivityInterface) {
    this._activity.set(activity);
  }
  get activity() {
    return this._activity();
  }

  private _showAvatar = true;
  @Input() set showAvatar(showAvatar: boolean) {
    this._showAvatar = showAvatar;
  }

  get showAvatar() {
    return this._showAvatar;
  }
  dateFormat = DateFormat.medium;

  isFormActivity = computed(() => {
    const activitySource = this.getStringFieldData(this.activity, StandardIds.ActivitySourceName);
    return activitySource === 'Form';
  });

  namespace = toSignal(this.config.namespace$);

  useBusinessCenterLink = toSignal(
    this.config.parentNamespace$.pipe(
      map((parentNamespace) => {
        return parentNamespace !== '';
      }),
    ),
  );

  cardInfo: Signal<ActivityCardInformation> = computed(() => {
    return {
      noteBody: this.getStringFieldData(this.activity, StandardIds.ActivityNoteBody),
      noteTitle: this.getStringFieldData(this.activity, StandardIds.ActivityNoteTitle),
      effectiveDate: this.getDateFieldData(this.activity, SystemFieldIds.ActivityEffectiveDate),
      activityUrl: this.getStringFieldData(this.activity, StandardIds.ActivityUrl),
      activityUrlName: this.getStringFieldData(this.activity, StandardIds.ActivityUrlName),
      formName: this.getStringFieldData(this.activity, StandardIds.ActivityRecordSourceDrillDown1),
      formLink: this.getFormLink(),
    };
  });

  private getStringFieldData(activity: ActivityInterface, fieldId: string): string {
    return this.fieldService.getFieldValueFromCrmObject(activity, fieldId)?.stringValue ?? '';
  }

  private getDateFieldData(activity: ActivityInterface, fieldId: string): Date | null {
    return this.fieldService.getFieldValueFromCrmObject(activity, fieldId)?.dateValue ?? null;
  }

  private getFormLink(): string | null {
    const formID = this.getStringFieldData(this.activity, StandardIds.ActivityRecordSourceDrillDown2);
    if (formID === '' || !this.isFormActivity()) {
      return null;
    }
    const link = `/custom-forms/edit/${formID}`;
    if (this.useBusinessCenterLink()) {
      return `/account/location/${this.namespace()}${link}`;
    }
    return link;
  }

  constructor(
    private readonly fieldService: CrmFieldService,
    @Inject(CrmInjectionToken) private readonly config: CrmDependencies,
  ) {}

  protected readonly AVATAR_WIDTH = AVATAR_WIDTH;

  headerText = computed(() => {
    const { noteTitle } = this.cardInfo();
    return noteTitle ?? '';
  });
}

interface ActivityCardInformation {
  noteBody: string;
  noteTitle: string;
  effectiveDate: Date | null;
  activityUrl: string | null;
  activityUrlName: string;
  formName: string;
  formLink: string | null;
}
